import { memo } from 'react'
import type { MouseEvent } from 'react'

export const WarningIcon = memo(({ size = 24 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      fill="#FF395D"
      fillOpacity="0.2"
    />
    <path
      d="M12 2.5C17.2467 2.5 21.5 6.75329 21.5 12C21.5 17.2467 17.2467 21.5 12 21.5C6.75329 21.5 2.5 17.2467 2.5 12C2.5 6.75329 6.75329 2.5 12 2.5Z"
      stroke="#FF395D"
      strokeOpacity="0.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 8V12"
      stroke="#FF395D"
      strokeOpacity="0.8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 16H12.01"
      stroke="#FF395D"
      strokeOpacity="0.8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
))
WarningIcon.displayName = 'WarningIcon'

export const YellowWarningIcon = memo(({ size = 24 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      fill="currentColor"
      fillOpacity="0.2"
    />
    <path
      d="M12 2.5C17.2467 2.5 21.5 6.75329 21.5 12C21.5 17.2467 17.2467 21.5 12 21.5C6.75329 21.5 2.5 17.2467 2.5 12C2.5 6.75329 6.75329 2.5 12 2.5Z"
      stroke="currentColor"
      strokeOpacity="0.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 8V12"
      stroke="currentColor"
      strokeOpacity="0.8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 16H12.01"
      stroke="currentColor"
      strokeOpacity="0.8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
))
YellowWarningIcon.displayName = 'YellowWarningIcon'

export const CircularProgressIcon = memo(
  ({ size = 24, progress = 75, onClick }: { size?: number; progress?: number; onClick?: (e?: MouseEvent) => void }) => {
    const viewBoxSize = 24
    const center = viewBoxSize / 2
    const radius = center - 2 // Leave 2px margin for 2px stroke width
    const circumference = 2 * Math.PI * radius
    const offset = circumference - (progress / 100) * circumference
    const strokeColor = progress > 90 ? 'var(--danger)' : 'var(--warning)'

    const svg = (
      <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}>
        {/* Background circle */}
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(255, 255, 255, 0.2)" strokeWidth="2" />
        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth="2"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
    )

    if (onClick) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onClick(e)
          }}
          className="cursor-pointer border-none bg-transparent p-0 transition-opacity hover:opacity-80"
          aria-label="View subscription details"
        >
          {svg}
        </button>
      )
    }

    return svg
  },
)
CircularProgressIcon.displayName = 'CircularProgressIcon'
